import gql from 'graphql-tag'
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
} from 'office-ui-fabric-react'
import React, { useState } from 'react'
import { ChildProps, graphql } from 'react-apollo'
import './SupplyChainConfiguration.css'
import { AddSupplyChainNodeDialog } from './AddSupplyChainNodeDialog'
import { Reference, updateReferenceChain } from '../../../../Api/references'
import { getFirstNodeLabel } from './utils'

interface InputProps {
  onClose: () => void
  onSaved: () => void
}

const gqlQuery = gql`
  query {
    sellerDomain {
      domain
    }
    session {
      accountDisplayName
    }
  }
`

interface GqlResponse {
  sellerDomain?: {
    domain: string
  }
  session: {
    accountDisplayName: string
  }
}

function SupplyChainConfigurationView({
  onClose,
  onSaved,
  data,
}: ChildProps<InputProps, GqlResponse>) {
  const [nodes, setNodes] = useState<Reference[]>([])
  const [isSubmitting, setSubmitting] = useState<boolean>(false)
  const [isAddNodeDialogVisible, setAddNodeDialogVisible] =
    useState<boolean>(false)
  const [isError, setError] = useState<boolean>(false)
  const submit = async () => {
    setSubmitting(true)
    setError(false)
    try {
      await updateReferenceChain({
        references: [...nodes]
          .reverse()
          .map(({ buyerDomain, seller_id }) => ({ buyerDomain, seller_id })),
      })
      setSubmitting(false)
      onSaved()
    } catch (err) {
      setError(true)
      setSubmitting(false)
      console.error(err)
    }
  }
  if (!data || data.loading) {
    return null
  }
  const accountDisplayName = data.session!.accountDisplayName
  const ownDeclarationDomain = data.sellerDomain
    ? data.sellerDomain.domain
    : undefined
  const firstNodeLabel = getFirstNodeLabel({
    accountDisplayName,
    ownDeclarationDomain,
    referencesFromPublisher: nodes.slice(0).reverse(),
  })
  const lastNode = nodes.length
    ? {
        label: nodes[0].buyerDomain,
        domain: nodes[0].buyerDomain,
      }
    : {
        label: ownDeclarationDomain || accountDisplayName,
        domain: ownDeclarationDomain,
      }
  return (
    <>
      <Dialog
        hidden={false}
        modalProps={{
          isDarkOverlay: true,
          isBlocking: true,
        }}
        onDismiss={onClose}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Supply Chain Configuration',
        }}
      >
        <Stack
          horizontalAlign="center"
          gap={24}
          style={{ paddingTop: 12, paddingBottom: 12 }}
        >
          <div className="supply-chain-node">
            <DefaultButton
              iconProps={{
                iconName: 'CircleAddition',
              }}
              disabled={false}
              onClick={() => setAddNodeDialogVisible(true)}
              text="Add node"
            />
          </div>
          {nodes.map((node, index) => (
            <div className="supply-chain-node" key={index}>
              {index === 0 ? (
                <DeletableNode
                  label={node.buyerDomain}
                  onDelete={() => setNodes(nodes.slice(1))}
                />
              ) : (
                <Node label={node.buyerDomain} />
              )}
            </div>
          ))}
          <div className="supply-chain-node">
            <Node label={firstNodeLabel} />
          </div>
        </Stack>
        {isError && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={true}
            styles={{ root: { marginTop: 10 } }}
          >
            Could not save supply chain configuration. If the problem persist,
            please contact support.
          </MessageBar>
        )}
        <Separator />
        <DialogFooter>
          <DefaultButton
            onClick={onClose}
            disabled={isSubmitting}
            text="Cancel"
          />
          <PrimaryButton
            onClick={submit}
            disabled={!nodes.length || isSubmitting}
            text="Save"
          />
        </DialogFooter>
      </Dialog>
      {isAddNodeDialogVisible ? (
        <AddSupplyChainNodeDialog
          onClose={() => setAddNodeDialogVisible(false)}
          previousNode={lastNode}
          onNode={(node: Reference) => {
            setNodes([node, ...nodes])
            setAddNodeDialogVisible(false)
          }}
        />
      ) : null}
    </>
  )
}

const withGql = graphql<InputProps, GqlResponse>(gqlQuery, {
  options: () => ({}),
})
export const SupplyChainConfiguration = withGql(SupplyChainConfigurationView)

function DeletableNode({
  label,
  onDelete,
}: {
  label: string
  onDelete: () => void
}) {
  const noop = () => {}
  return (
    <DefaultButton
      default
      primaryDisabled
      split
      text={label}
      className="delete"
      onMenuClick={onDelete}
      menuProps={{
        items: [],
      }}
      menuIconProps={{
        iconName: 'Delete',
        'aria-label': 'Delete',
      }}
      onClick={noop}
      styles={{
        flexContainer: {
          borderWidth: 0,
        },
        splitButtonMenuButton: {
          borderWidth: 1,
          borderTopColor: 'var(--color-border-light)',
          borderBottomColor: 'var(--color-border-light-35)',
          borderRightColor: 'var(--color-border-light-35)',
        },
      }}
    />
  )
}

function Node({ label }: { label: string }) {
  return <DefaultButton disabled text={label} />
}
